import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './CreateRestaurantModal.css';

import { API_URL } from '../../utils/api';

interface CreateRestaurantModalProps {
  onClose: () => void;
  onSuccess: (restaurantId: string) => void;
}

interface Restaurant {
  id: string;
  name: string;
  slug: string;
}

interface Venue {
  id: string;
  name: string;
  slug: string;
}

function CreateRestaurantModal({ onClose, onSuccess }: CreateRestaurantModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [error, setError] = useState('');
  const [existingRestaurants, setExistingRestaurants] = useState<Restaurant[]>([]);
  const [showExisting, setShowExisting] = useState(false);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [venueId, setVenueId] = useState<string>('');

  useEffect(() => {
    fetchExistingRestaurants().catch(() => {});
    axios.get(`${API_URL}/api/venues`).then((r) => setVenues(r.data || [])).catch(() => setVenues([]));
  }, []);

  const fetchExistingRestaurants = async () => {
    setLoadingRestaurants(true);
    try {
      const response = await axios.get(`${API_URL}/api/restaurants`);
      setExistingRestaurants(response.data || []);
      console.log('Loaded existing restaurants:', response.data?.length || 0);
    } catch (err: any) {
      console.error('Failed to load existing restaurants:', err);
      // Silently fail - restaurants list is optional, don't show scary error
      setExistingRestaurants([]);
    } finally {
      setLoadingRestaurants(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Auto-generate slug from name if not provided
    const finalSlug = slug.trim() || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

    try {
      const response = await axios.post(`${API_URL}/api/restaurants`, {
        name: name.trim(),
        slug: finalSlug,
        venueId: venueId || undefined,
      });
      onSuccess(response.data.id);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || t('modals.failedCreateRestaurant'));
      console.error('Create restaurant error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('modals.createRestaurant')}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-box">{error}</div>}

            <div className="form-group">
              <label>{t('modals.restaurantName')}</label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  // Auto-generate slug
                  if (!slug) {
                    setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
                  }
                }}
                required
                placeholder="e.g., McDonald's"
              />
            </div>

            <div className="form-group">
              <label>Venue (food court)</label>
              <select
                value={venueId}
                onChange={(e) => setVenueId(e.target.value)}
                className="form-select"
              >
                <option value="">— No venue —</option>
                {venues.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
              <small>Assign to a food court for cross-upsell with other restaurants</small>
            </div>

            <div className="form-group">
              <label>{t('modals.slug')}</label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                required
                placeholder="e.g., mcdonalds"
              />
              <small>{t('modals.slugHint')}</small>
              {slug && existingRestaurants.some(r => r.slug === slug) && (
                <small className="error-text">{t('modals.slugTaken')}</small>
              )}
              <button
                type="button"
                onClick={() => {
                  if (!showExisting) {
                    fetchExistingRestaurants();
                  }
                  setShowExisting(!showExisting);
                }}
                className="btn-show-existing"
              >
                {showExisting ? '▼' : '▶'} {existingRestaurants.length} {t('modals.existingRestaurants')} {existingRestaurants.length === 1 ? t('modals.restaurantCount') : t('modals.restaurantsCount')}
                {loadingRestaurants && ` (${t('modals.loadingRestaurants')})`}
              </button>
              {showExisting && (
                <div className="existing-restaurants-list">
                  {loadingRestaurants ? (
                    <small>{t('modals.loadingRestaurants')}</small>
                  ) : existingRestaurants.length === 0 ? (
                    <small>{t('modals.noRestaurantsFound')}</small>
                  ) : (
                    <>
                      <ul>
                        {existingRestaurants.map(r => (
                          <li key={r.id}>
                            <strong>{r.name}</strong> → <code>{r.slug}</code>
                            {slug === r.slug && <span className="slug-match"> {t('modals.slugTaken')}</span>}
                          </li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        onClick={fetchExistingRestaurants}
                        className="btn-refresh-list"
                      >
                        {t('modals.refreshList')}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-cancel">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={loading || !name.trim()} className="btn-submit">
              {loading ? t('modals.creatingRestaurant') : t('modals.createRestaurant')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateRestaurantModal;

